import { useCallback, useMemo, useState } from 'react'
import { useColumnAliases } from '@hooks/useColumnAliases'
import { ColumnAliasContext } from './columnAliasContext'

/**
 * Wraps a specialized report page so its grid can render the firm's column
 * names and publish its real columns back up for the rename editor
 * (CC-50291 - see `columnAliasContext.js` for the whole design).
 *
 * `reportKey` is the page's `actionKey`, the specialized half of the backend's
 * `report_key` (spec 061 section 2). With the feature flag off,
 * `useColumnAliases` returns the refusing shape, so this provider is inert
 * rather than merely empty.
 */

/**
 * Signature of a published column list, for change detection.
 *
 * JSON.stringify rather than joining with a delimiter: a column label is
 * arbitrary firm-typed text, so any separator we picked could appear inside a
 * label and make two different lists compare equal. It also avoids writing a
 * literal separator character into source at all - the first version of this
 * used a template literal whose separator was committed as a NUL byte, which
 * made git treat this file as binary and would have made shell grep go silent
 * on it, exactly as it already does for reportGridUtils.js.
 */
const columnsSignature = (list) =>
  JSON.stringify((list || []).map((c) => [String(c?.key ?? ''), String(c?.label ?? '')]))

const ColumnAliasProvider = ({ reportKey, children }) => {
  const { aliases, canManage } = useColumnAliases(reportKey)
  const [columns, setColumns] = useState([])

  // Identity-stable, and a no-op when the published list is unchanged. Both
  // matter: the grids call this from an effect on every data change, so a new
  // function each render - or an unconditional setState - would loop.
  const publishColumns = useCallback((next) => {
    setColumns((prev) => (columnsSignature(prev) === columnsSignature(next) ? prev : (next || [])))
  }, [])

  const value = useMemo(
    () => ({ reportKey, aliases, canManage, columns, publishColumns }),
    [reportKey, aliases, canManage, columns, publishColumns]
  )

  return <ColumnAliasContext.Provider value={value}>{children}</ColumnAliasContext.Provider>
}

export default ColumnAliasProvider
